/**
 * src/i18n/fr.twoFactorParticipant.ts
 *
 * Rôle :
 *   Sous-catalogue i18n de l'application d'authentification des PARTICIPANTS
 *   (exposants, animateurs, candidats). Fusionné dans `messagesFr` par
 *   `fr.ts`, sur le modèle de `fr.twoFactor.ts`.
 *
 *   Le vocabulaire commun du parcours guidé (installer, relier, vérifier)
 *   reste dans `fr.twoFactor.ts` et est RÉUTILISÉ tel quel : on n'écrit ici
 *   que ce qui est propre aux participants.
 *
 * Règles tenues ici :
 *   1. Le courriel est TOUJOURS présenté comme la méthode par défaut, et
 *      l'application comme un ajout. Aucune phrase ne dit « remplacer ».
 *   2. Aucune mention de code de secours : le courriel EST le secours.
 *   3. Aucune identité en dur : le nom de l'événement passe par {eventName},
 *      alimenté par useEventIdentity().
 *   4. Apostrophe typographique partout (une apostrophe droite devant une
 *      accolade ICU ferait disparaître le token).
 *   5. Pas de tiret cadratin.
 *
 * Mikael - chef de projet - 2026-09-28
 */

export const messagesTwoFactorParticipantFr: Record<string, string> = {
  // ── Encart de « Mon profil » ─────────────────────────────────────────
  'twoFactorParticipant.card.title': 'Comment vous recevez votre code de connexion',
  'twoFactorParticipant.card.email':
    'Votre code de connexion à {eventName} arrive par courriel. C’est la méthode par défaut, et elle reste toujours possible.',
  'twoFactorParticipant.card.offer':
    'Vous pouvez ajouter en plus une application d’authentification (Google Authenticator, Microsoft Authenticator ou une autre) : le code s’affiche alors directement sur votre téléphone, sans attendre le courriel.',
  'twoFactorParticipant.card.add': 'Ajouter une application',
  'twoFactorParticipant.card.linked.since':
    'Une application d’authentification est liée à votre compte depuis le {date}.',
  'twoFactorParticipant.card.linked.sinceUnknown':
    'Une application d’authentification est liée à votre compte.',
  'twoFactorParticipant.card.linked.emailStill':
    'Le courriel reste toujours possible : à la connexion, le lien « Recevoir plutôt un code par courriel » est toujours à l’écran.',
  'twoFactorParticipant.card.remove': 'Retirer l’application',
  'twoFactorParticipant.card.remove.title': 'Retirer l’application de votre compte ?',
  'twoFactorParticipant.card.remove.body':
    'Votre application ne donnera plus de code valable pour ce compte. Vous recevrez votre code par courriel, comme avant, et vous pourrez lier une application à nouveau quand vous voudrez.',
  'twoFactorParticipant.card.remove.confirm': 'Oui, retirer l’application',
  'twoFactorParticipant.card.remove.cancel': 'Annuler',
  'twoFactorParticipant.card.removed':
    'Application retirée. Vous recevrez votre code par courriel.',

  // ── Parcours guidé partagé (profil et inscription) ────────────────────
  'twoFactorParticipant.wizard.title': 'Ajouter une application d’authentification',
  'twoFactorParticipant.wizard.intro':
    'Votre code continuera d’arriver par courriel si vous le demandez : l’application est un raccourci en plus.',
  'twoFactorParticipant.wizard.preparing': 'Préparation de votre clé…',
  'twoFactorParticipant.wizard.cancel': 'Annuler',
  'twoFactorParticipant.wizard.skip': 'Continuer sans',
  'twoFactorParticipant.wizard.done.title': 'C’est fait : votre application est liée',
  'twoFactorParticipant.wizard.done.body':
    'À votre prochaine connexion, saisissez le code affiché par votre application. Le lien « Recevoir plutôt un code par courriel » restera toujours à l’écran.',
  'twoFactorParticipant.wizard.done.close': 'Terminer',

  // ── Étape de l'inscription ────────────────────────────────────────────
  'twoFactorParticipant.etape.titre': 'Recevoir votre code plus vite',
  'twoFactorParticipant.etape.compteCree': 'Votre compte est créé. Vous êtes inscrit·e.',
  'twoFactorParticipant.etape.corps':
    'Pour vous connecter à {eventName}, votre code arrive par courriel. C’est la méthode par défaut, et elle reste toujours possible.',
  'twoFactorParticipant.etape.offre':
    'Vous pouvez ajouter en plus une application d’authentification sur votre téléphone : le code s’y affiche directement, sans attendre le courriel.',
  'twoFactorParticipant.etape.ajouter': 'Ajouter une application',
  'twoFactorParticipant.etape.continuer': 'Continuer sans',
  'twoFactorParticipant.etape.plusTard':
    'Vous pourrez aussi le faire plus tard, depuis « Mon profil ».',
  'twoFactorParticipant.etape.chargement': 'Un instant…',

  // ── Connexion (/connexion) ────────────────────────────────────────────
  'twoFactorParticipant.login.subtitleApp':
    'Saisissez le code affiché par votre application d’authentification.',
  'twoFactorParticipant.login.account': 'Compte : {email}',
  'twoFactorParticipant.login.legendApp': 'Code à 6 chiffres affiché par votre application',
  'twoFactorParticipant.login.forceEmail': 'Recevoir plutôt un code par courriel',
  'twoFactorParticipant.login.appAlsoAccepted':
    'Le code affiché par votre application d’authentification est aussi accepté.',
  // 2026-09-28 (revue adversariale, constat 1) : en mode application, un code
  // refusé renvoie vers le courriel, jamais vers « demandez un nouveau code »
  // (aucun code n'a été demandé : il s'affiche sur le téléphone).
  'twoFactorParticipant.login.codeInvalidApp':
    'Ce code n’est pas valide. Vérifiez que l’heure de votre téléphone est réglée automatiquement, ou choisissez « Recevoir plutôt un code par courriel ».',
  // Plafond d'échecs atteint : le serveur a coupé le chemin application,
  // l'écran bascule d'office vers le courriel.
  'twoFactorParticipant.login.appCoupee':
    'Le code de votre application n’est plus accepté pour le moment. Nous venons de vous envoyer un code par courriel.',
  'twoFactorParticipant.login.appCoupeeCooldown':
    'Le code de votre application n’est plus accepté pour le moment. Un code vous a déjà été envoyé par courriel il y a moins d’une minute : saisissez-le.',
  'twoFactorParticipant.login.appCoupeeEchec':
    'Le code de votre application n’est plus accepté pour le moment. Utilisez « Renvoyer un code » pour recevoir un code par courriel.',

  // ── Erreurs (lues sur le `code` machine, jamais sur le texte) ─────────
  'twoFactorParticipant.error.codeInvalid':
    'Ce code n’est pas valide. Vérifiez que l’heure de votre téléphone est réglée automatiquement : le code change toutes les 30 secondes.',
  'twoFactorParticipant.error.codeInvalidWithAttempts':
    'Ce code n’est pas valide. Vérifiez que l’heure de votre téléphone est réglée automatiquement. Il vous reste {attempts, plural, one {# essai} other {# essais}}.',
  // 2026-09-28 (revue adversariale, constat 4) : la nouvelle clé remplace
  // l'ancienne. La ligne déjà scannée resterait dans le téléphone et ne
  // donnerait que des codes refusés : on dit de la supprimer d'abord.
  'twoFactorParticipant.error.noPendingSetup':
    'La liaison a expiré et une nouvelle clé vient d’être préparée. Dans votre application, supprimez d’abord la ligne ajoutée tout à l’heure pour ce compte : elle ne donnerait que des codes refusés. Scannez ensuite le nouveau QR code ci-dessous.',
  'twoFactorParticipant.error.impersonation':
    'Une application d’authentification ne peut être liée que par la personne elle-même, depuis sa propre connexion.',
  'twoFactorParticipant.error.backofficeApp':
    'Ce compte gère sa double authentification depuis l’espace d’administration (page Sécurité). Votre code de connexion participant arrive par courriel.',
  'twoFactorParticipant.error.methodNotAllowed':
    'La connexion par application n’est pas proposée pour le moment. Votre code de connexion arrive toujours par courriel, vous n’avez rien à faire.',
  'twoFactorParticipant.error.rateLimited':
    'Trop de tentatives. Patientez quelques minutes avant de réessayer. Votre code par courriel reste disponible.',
  // 2026-09-28 (revue adversariale, constat 6) : le délai donné par le serveur.
  'twoFactorParticipant.error.rateLimitedSeconds':
    'Trop de tentatives. Réessayez dans {seconds, plural, one {# seconde} other {# secondes}}. Votre code par courriel reste disponible.',
  'twoFactorParticipant.error.rateLimitedMinutes':
    'Trop de tentatives. Réessayez dans {minutes, plural, one {# minute} other {# minutes}}. Votre code par courriel reste disponible.',
  'twoFactorParticipant.error.session':
    'Votre session a expiré. Reconnectez-vous avec le code reçu par courriel, puis recommencez.',
  'twoFactorParticipant.error.unavailable':
    'Le service ne répond pas pour le moment. Réessayez dans un instant : votre code par courriel reste disponible.',
  'twoFactorParticipant.error.network':
    'La demande n’a pas abouti. Vérifiez votre connexion, puis réessayez.',
  // 2026-09-28 (revue adversariale, constat 5) : réseau coupé pendant
  // l'activation. Le serveur a pu lier : « Réessayer » relit d'abord l'état.
  'twoFactorParticipant.error.networkVerify':
    'La vérification n’a pas abouti : la connexion a été interrompue. Votre application a peut-être été liée quand même. « Réessayer » le vérifie, sans rien refaire en double.',
  'twoFactorParticipant.wizard.retry': 'Réessayer',
};
